import { compareDesc } from 'date-fns'
import { GeckoAggregation } from 'types/cdfParams/geckoAggregation'
import { GeckoAlgorithm } from 'types/cdfParams/geckoAlgorithm'
import { GeckoAnglePlotSetting } from 'types/cdfParams/geckoAnglePlotSetting'
import { GeckoBinSize } from 'types/cdfParams/geckoBinSize'
import { GeckoSource } from 'types/cdfParams/geckoSource'
import { GeckoTMinMethod } from 'types/cdfParams/geckoTMinMethod'
import { GeckoView } from 'types/cdfParams/geckoView'
import { GeckoPublishingState } from 'types/enums/geckoPublishingState'
import { GeckoComponentCoords } from 'types/geometric/geckoComponentCoords'
import { GeckoComponentRect } from 'types/geometric/geckoComponentRect'
import { GeckoMeasurementUnit } from 'types/measurement/geckoMeasurementUnit'
import {
  parseBoolean,
  parseDate,
  parseNumber,
  parseString,
} from 'utils/parsingHelpers'

import { GeckoComponentDataset } from './geckoComponentDataset'
import { GeckoEntryBase } from './geckoEntry'
import { RolodexEntryJson } from './rolodexEntryFields'

const CDF_SCHEMA_VERSION = 14

/**
 * Component data frames represent 2-dimensional data projected down onto the
 * surface of a component. These include thickness C-scans, but also extend
 * to derived views, like difference-from nominal, etc.
 */
export class GeckoComponentDataFrame extends GeckoEntryBase<
  {
    geometry: GeckoComponentRect
    binning: {
      bin_size: GeckoBinSize
      x_bin_count: number
      y_bin_count: number
      offset: GeckoComponentCoords
    }
    params: ComponentDataFrameParams
    is_default: boolean | undefined
    schema_version: number
    _parquet_column_name: string | undefined
    parquet_file_name: string | undefined
  },
  {
    organization: string
    site: string
    unit: string
    component: string
    job?: string
    inspection?: string
    'component-dataset'?: string
  },
  Record<string, never>
> {
  //----------------------------------------------------------------------------

  /**
   * A simple constructor, private so that a more descriptive factory method
   * (which can return undefined) is used instead
   * @param json The raw JSON to create an instance from
   * @private
   */
  private constructor(json: RolodexEntryJson) {
    //--------------------------------------------------------------------------
    const binning = (json.data.binning as Record<string, unknown>) ?? {}
    const params = (json.data.params as Record<string, unknown>) ?? {}
    const geometry = (json.data.geometry as Record<string, unknown>) ?? {}

    const component = parseString(json.tags.component)
    if (!component) throw new Error('CDF missing component tag')

    const rect = GeckoComponentRect.CreateFromJSON(component, geometry)
    if (!rect)
      throw new Error(
        `CDF geometry malformed for ${json.id}: ${JSON.stringify(geometry)}`
      )

    const bin_width = parseNumber(binning.bin_width)
    const bin_height = parseNumber(binning.bin_height)
    const x_bin_count = parseNumber(binning.x_bin_count)
    const y_bin_count = parseNumber(binning.y_bin_count)
    const x_offset = parseNumber(binning.x_offset) ?? 0
    const y_offset = parseNumber(binning.y_offset) ?? 0
    if (
      bin_width === undefined ||
      bin_height === undefined ||
      x_bin_count === undefined ||
      y_bin_count === undefined
    )
      throw new Error('CDF binning information missing')

    //--------------------------------------------------------------------------

    super(
      json,
      {
        geometry: rect,
        binning: {
          bin_size: GeckoBinSize.GetOrCreate(
            bin_width,
            rect.x.unit,
            bin_height,
            rect.y.unit
          ),
          x_bin_count,
          y_bin_count,
          offset: GeckoComponentCoords.CreateFromValues(
            rect.componentID,
            x_offset,
            rect.x.unit,
            y_offset,
            rect.y.unit
          ),
        },
        params: getCDFParams(params),
        is_default: parseBoolean(json.data.is_default) ?? false,
        schema_version:
          parseNumber(json.data.schema_version) ?? CDF_SCHEMA_VERSION,
        _parquet_column_name: parseString(json.data._parquet_column_name) ?? '',
        parquet_file_name: parseString(json.data.parquet_file_name) ?? '',
      },
      {
        organization: parseString(json.tags.organization) ?? '',
        site: parseString(json.tags.site) ?? '',
        unit: parseString(json.tags.unit) ?? '',
        component: parseString(json.tags.component) ?? '',
        job: parseString(json.tags.job),
        inspection: parseString(json.tags.inspection),
      },
      {}
    )
  }

  //----------------------------------------------------------------------------

  /**
   * A simple factory method which creates a component data frame from raw JSON
   * @param json The raw JSON to create an instance from
   * @constructor
   */
  public static CreateFromJSON(json: RolodexEntryJson) {
    return new GeckoComponentDataFrame(json)
  }

  public static GetDefaultSort(
    componentDatasets: readonly GeckoComponentDataset[]
  ) {
    return (a: GeckoComponentDataFrame, b: GeckoComponentDataFrame) =>
      GeckoComponentDataFrame.DefaultSort(a, b, componentDatasets)
  }

  public static DefaultSort(
    a: GeckoComponentDataFrame,
    b: GeckoComponentDataFrame,
    componentDatasets: readonly GeckoComponentDataset[]
  ) {
    const aIsDefault = a.data.is_default
    const bIsDefault = b.data.is_default
    if (aIsDefault !== bIsDefault) return aIsDefault ? -1 : 1

    const cdsA = componentDatasets?.find(
      (cds) => cds.id === a.tags['component-dataset']
    )
    const cdsB = componentDatasets?.find(
      (cds) => cds.id === b.tags['component-dataset']
    )
    if (
      !!cdsA &&
      !!cdsB &&
      cdsA.data.publishing_state !== cdsB.data.publishing_state
    )
      return GeckoPublishingState.DefaultSort(
        cdsA.data.publishing_state,
        cdsB.data.publishing_state
      )

    // We need to adjust behavior if there are RAUT datasets present
    const hasAdvancedServices =
      [GeckoSource.RAUT, GeckoSource.TRILAT].includes(a?.data.params.source) ||
      [GeckoSource.RAUT, GeckoSource.TRILAT].includes(b?.data.params.source)
    // Date usually refers to the date of the inspection, so if it's provided on 2 CDFs we can get the most recent
    const dateA = a.data.params.date
    const dateB = b.data.params.date
    const dateComparison =
      !!dateA && !!dateB ? compareDesc(dateA, dateB) : Number.NaN
    if (!Number.isNaN(dateComparison) && dateComparison !== 0)
      return dateComparison
    const viewA = a.data.params.view
    const viewB = b.data.params.view
    if (viewA !== viewB) {
      if (hasAdvancedServices) {
        if (viewA === GeckoView.Thickness) return -1
        if (viewB === GeckoView.Thickness) return 1
      }
      return GeckoView.DefaultSort(viewA, viewB)
    }
    const sourceA = a.data.params.source
    const sourceB = b.data.params.source
    if (sourceA !== sourceB) return GeckoSource.DefaultSort(sourceA, sourceB)
    const algA = a.data.params.algorithm
    const algB = b.data.params.algorithm

    if (algA !== algB) {
      if (hasAdvancedServices) {
        if (algA.id === 'cscans-selected-measure') return -1
        if (algB.id === 'cscans-selected-measure') return 1
      } else {
        GeckoAlgorithm.DefaultSort(algA, algB)
      }
    }
    const aggA = a.data.params.aggregation
    const aggB = b.data.params.aggregation
    if (aggA !== aggB) return GeckoAggregation.DefaultSort(aggA, aggB)
    const apsA = a.data.params.angle_plot_setting
    const apsB = b.data.params.angle_plot_setting
    if (apsA !== apsB) return GeckoAnglePlotSetting.DefaultSort(apsA, apsB)
    const tMinA = a.data.params.tmin_method
    const tMinB = b.data.params.tmin_method
    if (tMinA !== tMinB) return GeckoTMinMethod.DefaultSort(tMinA, tMinB)
    const depthA = a.data.params.depth ?? Number.NEGATIVE_INFINITY
    const depthB = b.data.params.depth ?? Number.NEGATIVE_INFINITY
    if (depthA !== depthB) return depthB - depthA
    const binSizeA = a.data.binning.bin_size
    const binSizeB = b.data.binning.bin_size
    if (hasAdvancedServices) {
      return GeckoBinSize.DefaultSort(binSizeA, binSizeB) * -1
    } else {
      return GeckoBinSize.DefaultSort(binSizeA, binSizeB)
    }
  }
}

export function getCDFParams(params: Record<string, unknown>) {
  const viewID = parseString(params.view)
  if (!viewID)
    throw new Error(`CDF missing view param: ${JSON.stringify(params)}`)

  const sourceID = parseString(params.source)
  if (!sourceID)
    throw new Error(`CDF missing source param: ${JSON.stringify(params)}`)

  const measurementUnit = GeckoMeasurementUnit.GetOrCreate(
    parseString(params.measurement_unit) ?? ''
  )

  return {
    view: GeckoView.GetOrCreate(viewID),
    source: GeckoSource.GetOrCreate(sourceID),
    algorithm: GeckoAlgorithm.GetOrCreate(parseString(params.algorithm)),
    aggregation: GeckoAggregation.GetOrCreate(parseString(params.aggregation)),
    angle_plot_setting: GeckoAnglePlotSetting.GetOrCreate(
      parseNumber(params.angle_plot_setting)
    ),
    tmin_method: GeckoTMinMethod.GetOrCreate(parseString(params.tmin_method)),
    measurement_unit: measurementUnit,
    date: parseDate(params.date),
    depth: parseNumber(params.depth),
    api_653_constants: undefined,
  }
}
export interface ComponentDataFrameParams {
  view: GeckoView
  source: GeckoSource
  aggregation: GeckoAggregation
  algorithm: GeckoAlgorithm
  angle_plot_setting: GeckoAnglePlotSetting
  tmin_method: GeckoTMinMethod
  measurement_unit: GeckoMeasurementUnit
  date: Date | undefined
  depth: number | undefined
  api_653_constants:
    | {
        specific_gravity: number
        upper_courses_stress_tolerance: number
        lower_courses_stress_tolerance: number
        fill_height: number
      }
    | undefined
}
